'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var React = require('react');
require('../utilities/color.js');
require('date-fns');
require('../utilities/locale.js');
var number = require('../utilities/number.js');
var touch = require('../utilities/touch.js');

/**
 * React hook for managing scale state for use with CSS `scale: 1` or `transform: scale(1)`.
 *
 * @params UseScaleOptions
 * @returns UseScaleState
 */ const useScale = (options = {})=>{
    const { enableMouseWheel =true , enableTouch =true , enableTrackpad =true , enabled =true , ref  } = options;
    let { scaleInMax =3 , scaleInSteps =4 , scaleOutMax =0.25 , scaleOutSteps =3  } = options;
    scaleInMax = scaleInMax < 1 ? 1 : scaleInMax;
    scaleInSteps = scaleInSteps < 0 ? 0 : scaleInSteps;
    scaleOutMax = scaleOutMax <= 0.01 ? 0.01 : scaleOutMax;
    scaleOutSteps = scaleOutSteps < 0 ? 0 : scaleOutSteps;
    /**
   * These don't change when `scale` changes
   */ const baseScale = 1;
    const scaleInIncrement = (scaleInMax - baseScale) / scaleInSteps; // 0.5 with defaults float((scaleInMax - baseScale) / scaleInSteps)
    const scaleOutIncrement = (baseScale - scaleOutMax) / scaleOutSteps; // 0.25 with defaults float((baseScale - scaleOutMax) / scaleOutSteps)
    const scaleRange = [
        // [0.25, 0.5, 0.75] with defaults
        ...number.getNumberRange({
            increment: scaleOutIncrement,
            length: scaleOutSteps,
            operator: '+',
            start: scaleOutMax
        }),
        baseScale,
        // [1.5, 2, 2.5, 3] with defaults
        ...number.getNumberRange({
            increment: scaleInIncrement,
            length: scaleInSteps,
            operator: '-',
            start: scaleInMax
        }).reverse()
    ];
    /**
   * Tracks the touch start distance to compare to touch move
   */ const distanceRef = React.useRef(0);
    /**
   * Tracks if actively scaling via TouchEvent on the container. We need both ref and state
   * (ref is needed when used inside event listeners, state variable doesn't update there)
   */ const scalingRef = React.useRef(false);
    const [scaling, setScaling] = React.useState(false);
    /**
   * Tracks the current scale state
   */ const [scale, setScale] = React.useState(baseScale);
    /**
   * These change when `scale` changes
   */ const scaledIn = scale > baseScale;
    const scaledInMax = scaleInSteps === 0 || scale >= scaleInMax;
    const scaledOut = scale < baseScale;
    const scaledOutMax = scaleOutSteps === 0 || scale <= scaleOutMax;
    const reset = ()=>{
        setScale(baseScale);
    };
    const getHasNextScale = (nextScale)=>{
        return scaleRange.find((s)=>number.getNumberCloselyMatches(s, nextScale));
    };
    const getClosestScaleIn = (prevScale)=>{
        if (scaleRange.length === 0) return prevScale;
        if (scaleRange.length === 1) return scaleRange[0];
        const filtered = scaleRange.filter((n)=>n >= prevScale);
        if (filtered.length === 0) return prevScale;
        return filtered[0];
    };
    const getClosestScaleOut = (prevScale)=>{
        if (scaleRange.length === 0) return prevScale;
        if (scaleRange.length === 1) return scaleRange[0];
        const filtered = scaleRange.filter((n)=>n <= prevScale);
        if (filtered.length === 0) return prevScale;
        return filtered.at(-1);
    };
    const scaleIn = ()=>{
        setScale((prevScale)=>{
            const nextScale = scaledOut ? prevScale + scaleOutIncrement : prevScale + scaleInIncrement;
            const hasNextScale = getHasNextScale(nextScale);
            const newScale = hasNextScale ?? getClosestScaleIn(prevScale);
            return Math.min(Math.max(scaleOutMax, newScale), scaleInMax);
        });
    };
    const scaleOut = ()=>{
        setScale((prevScale)=>{
            const nextScale = scaledIn ? prevScale - scaleInIncrement : prevScale - scaleOutIncrement;
            const hasNextScale = getHasNextScale(nextScale);
            const newScale = hasNextScale ?? getClosestScaleOut(prevScale);
            return Math.min(Math.max(scaleOutMax, newScale), scaleInMax);
        });
    };
    const touchCancel = ()=>{
        scalingRef.current = false;
        setScaling(false);
        distanceRef.current = 0;
    };
    const onTouchMove = React.useCallback((e)=>{
        if (scalingRef.current && touch.getIsTouchPinch(e.touches)) {
            e.preventDefault();
            setScale(Math.min(Math.max(scaleOutMax, touch.getTouchDistance(e.touches) / distanceRef.current), scaleInMax));
        }
    }, [
        scaleInMax,
        scaleOutMax
    ]);
    const onTouchStart = (e)=>{
        if (touch.getIsTouchPinch(e.touches)) {
            scalingRef.current = true;
            setScaling(true);
            distanceRef.current = touch.getTouchDistance(e.touches);
        }
    };
    const onWheel = React.useCallback((e)=>{
        e.preventDefault();
        /**
       * NOTE: It's impossible to 100% detect mouse wheel vs trackpad (there is no JS API for this)
       * Trackpads always emit `ctrlKey` as `true`, but so do mouse wheels with `ctrlKey` pressed
       * See https://stackoverflow.com/q/10744645
       */ const isTrackpad = enableTrackpad && e.ctrlKey;
        const isMouseWheel = enableMouseWheel && !e.ctrlKey;
        if (isTrackpad || isMouseWheel) {
            const { deltaY  } = e;
            setScale((prevScale)=>{
                return Math.min(Math.max(scaleOutMax, isTrackpad ? prevScale - deltaY * 0.01 : prevScale - deltaY * 0.0025), scaleInMax);
            });
        }
    }, [
        enableTrackpad,
        enableMouseWheel,
        scaleInMax,
        scaleOutMax
    ]);
    React.useEffect(()=>{
        const htmlElement = ref?.current;
        if (!enabled || !htmlElement) return;
        if (enableTouch) {
            htmlElement.addEventListener('touchcancel', touchCancel);
            htmlElement.addEventListener('touchend', touchCancel);
            htmlElement.addEventListener('touchmove', onTouchMove);
            htmlElement.addEventListener('touchstart', onTouchStart);
        }
        if (enableTrackpad || enableMouseWheel) {
            htmlElement.addEventListener('wheel', onWheel);
        }
        return ()=>{
            if (enableTouch) {
                htmlElement.removeEventListener('touchcancel', touchCancel);
                htmlElement.removeEventListener('touchend', touchCancel);
                htmlElement.removeEventListener('touchmove', onTouchMove);
                htmlElement.removeEventListener('touchstart', onTouchStart);
            }
            if (enableTrackpad || enableMouseWheel) {
                htmlElement.removeEventListener('wheel', onWheel);
            }
        };
    }, [
        enableMouseWheel,
        enableTouch,
        enableTrackpad,
        enabled,
        onTouchMove,
        onWheel,
        ref
    ]);
    return {
        reset,
        scale,
        scaleIn,
        scaleOut,
        scaled: scale !== baseScale,
        scaledIn,
        scaledInMax,
        scaledOut,
        scaledOutMax,
        scaling
    };
};

exports.useScale = useScale;
//# sourceMappingURL=use-scale.js.map
